/** /tour_operators/{id}/sandbox/… — a tenant's sandbox environment (dev-platform metaplan S3, F2, S5a,
 *  S6a; SBX plan D3–D8).
 *
 *  A sandbox is its own `Tour_Operator` (`kind=sandbox`, code `<production code>sbx`) paired with its
 *  production operator. Ops read its state under `console_sandbox_view` and change it under
 *  `console_sandbox_manage`; "Add sandbox" also needs `console_top_manage` (it creates a tenant).
 *  Every write is audited server-side (`Tour_Operator_Config_Change` + the console audit log).
 *
 *  Shapes come from the generated spec types. Refusals answer `{detail, message, code, errors?}`
 *  (`errors`: per-entry `{field, error}`) — `sandboxErrorMessage` words them. */
import { ConsoleApiError, apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type SandboxState = Schemas['ConsoleSandboxState'];
export type SandboxAllowlist = Schemas['ConsoleSandboxAllowlist'];
export type OutboundMode = Schemas['ConsoleSandboxOptions']['default_outbound_mode'];
export type SandboxSuspension = Schemas['ConsoleSandboxSuspension'];
export type SandboxBreaker = Schemas['ConsoleSandboxBreaker'];
export type SandboxBreakerDetail = Schemas['ConsoleSandboxBreakerDetail'];
export type SandboxBreakerResetResult = Schemas['ConsoleSandboxBreakerResetResult'];
export type BreakerAction = Schemas['ConsoleSandboxBreakerReset']['action'];
export type SandboxIngress = Schemas['ConsoleSandboxIngress'];
export type IngressMode = SandboxIngress['mode'];
export type SandboxThrottle = NonNullable<Schemas['ConsoleSandboxThrottle']>;
export type SandboxQuotas = Schemas['ConsoleSandboxQuotas'];
export type SandboxQuotaSet = Schemas['ConsoleSandboxQuotaSet'];
export type QuotaKey = keyof SandboxQuotaSet;
export type SandboxQuotaOverrides = Schemas['ConsoleSandboxQuotasUpdate']['quotas'];
export type SandboxConfigDiff = Schemas['ConsoleSandboxConfigDiff'];
export type SandboxConfigDifference = SandboxConfigDiff['differences'][number];
/** Every field has a server default (generated types mark defaulted fields required). */
export type SandboxCreateInput = Partial<Schemas['ConsoleSandboxCreateInput']>;
export type SandboxProvisioned = Schemas['ConsoleSandboxProvisioned'];
export type SandboxOptions = Schemas['ConsoleSandboxOptions'];
export type SandboxBannerEntry = Schemas['ConsoleSandboxBannerEntry'];
export type SandboxProductionKeyEntry = Schemas['ConsoleSandboxProductionKeyEntry'];
export type SandboxCloneReport = NonNullable<Schemas['ConsoleSandboxCloneReport']>;
export type SandboxResetState = NonNullable<Schemas['ConsoleSandboxResetState']>;
/** The scheduled pull (S6c; SBX D7 §8, D4 §3, O29): the lane pause, the capped cadence, per restech
 *  account whether a scheduled pull runs, and the shared-vendor-quota warning. */
export type SandboxPull = NonNullable<Schemas['ConsoleSandboxPull']>;
export type SandboxPullRestech = Schemas['ConsoleSandboxPullRestech'];
export type SandboxPullBackoff = NonNullable<SandboxPullRestech['backoff']>;
export type ScheduledPull = SandboxPullRestech['scheduled_pull'];
export type PullPausedReason = NonNullable<SandboxPull['paused_reason']>;
/** A sandbox's own SMS number (DSEC D5): set up by its developers in the dashboard; ops see it and
 *  remove it. */
export type SandboxSms = Schemas['ConsoleSandboxSms'];
/** A sandbox's admin (DSEC D6): a staff user in a full-access or `operator_admin_*` group, or recorded at
 *  creation and still waiting for that group (`pending`). "Open sandbox" from production signs in as the
 *  first active one. */
export type SandboxAdmin = Schemas['ConsoleSandboxAdmin'];
export type SandboxAdminMembership = SandboxAdmin['membership'];
export type SandboxAdminAddInput = Schemas['ConsoleSandboxAdminAdd'];
export type SandboxAdminAdded = Schemas['ConsoleSandboxAdminAdded'];

/** The quotas in the order ops read them (SBX O5 defaults: 10k total, 500/day, 50/payload, 60 rpm). */
export const QUOTA_KEYS: readonly QuotaKey[] = [
  'bookings_per_day',
  'bookings_total',
  'bookings_per_payload',
  'requests_per_minute',
];

/** Query keys — under `tourOperators`, so a tenant-wide invalidation refreshes them too. */
export const sandboxKeys = {
  state: (topId: number) => ['tourOperators', 'sandbox', topId] as const,
  breaker: (topId: number) => ['tourOperators', 'sandbox', topId, 'breaker'] as const,
  ingress: (topId: number) => ['tourOperators', 'sandbox', topId, 'ingress'] as const,
  configDiff: (topId: number) => ['tourOperators', 'sandbox', topId, 'config-diff'] as const,
  options: ['tourOperators', 'sandbox-options'] as const,
};

const base = (topId: number) => `/tour_operators/${topId}/sandbox/`;

export const sandboxApi = {
  /** The state for ops: a sandbox's outbound, production key, suspension, quotas, throttle, breaker,
   *  ingress, clone and reset; a production tenant's paired sandbox and its production-key banner. */
  state(topId: number): Promise<SandboxState> {
    return apiGet(base(topId));
  },
  /** Mode and/or allowlist; each given key replaces the stored one. */
  updateOutbound(
    topId: number,
    body: { outbound: { mode?: OutboundMode; allowlist?: SandboxAllowlist }; comment?: string },
  ): Promise<SandboxState> {
    return apiPatch(base(topId), body);
  },
  /** "Add sandbox" on a production operator. Called straight from the form, never through
   *  `useMutation`: the body may carry the sandbox admin's password. */
  create(topId: number, body: SandboxCreateInput): Promise<SandboxProvisioned> {
    return apiPost(base(topId), body);
  },
  setSuspension(
    topId: number,
    body: { suspended: boolean; reason?: string; comment?: string },
  ): Promise<SandboxSuspension> {
    return apiPost(`${base(topId)}suspension/`, body);
  },
  reset(topId: number): Promise<SandboxResetState> {
    return apiPost(`${base(topId)}reset/`, {});
  },
  setQuotas(
    topId: number,
    body: { quotas: SandboxQuotaOverrides; comment?: string },
  ): Promise<SandboxQuotas> {
    return apiPatch(`${base(topId)}quotas/`, body);
  },
  configDiff(topId: number): Promise<SandboxConfigDiff> {
    return apiGet(`${base(topId)}config_diff/`);
  },
  breaker(topId: number): Promise<SandboxBreakerDetail> {
    return apiGet(`${base(topId)}breaker/`);
  },
  resetBreaker(
    topId: number,
    body: { action: BreakerAction; comment?: string },
  ): Promise<SandboxBreakerResetResult> {
    return apiPost(`${base(topId)}breaker/`, body);
  },
  ingress(topId: number): Promise<SandboxIngress> {
    return apiGet(`${base(topId)}ingress/`);
  },
  setIngress(
    topId: number,
    body: { mode: IngressMode; comment?: string },
  ): Promise<SandboxIngress> {
    return apiPatch(`${base(topId)}ingress/`, body);
  },
  /** Remove the sandbox's own SMS number: release it, delete its Conversations service (DSEC D5). */
  removeSms(topId: number): Promise<SandboxSms> {
    return apiDelete(`${base(topId)}sms/`);
  },
  /** Add an admin to the sandbox (DSEC D6): a new dashboard user in its admin group; without a password it
   *  signs in from production only. Called straight from the form, never through `useMutation`: the
   *  body may carry the admin's password. */
  addAdmin(topId: number, body: SandboxAdminAddInput): Promise<SandboxAdminAdded> {
    return apiPost(`${base(topId)}admins/`, body);
  },
  /** What the wizard's "Create a sandbox too" and "Add sandbox" may offer. */
  options(): Promise<SandboxOptions> {
    return apiGet('/tour_operators/sandbox_options/');
  },
};

/** A readable message from a sandbox API error: `{detail}` with the first per-entry reason
 *  (`errors: [{field, error}]`) when there is one, or DRF's field-keyed `{field: ["…"]}`. */
export function sandboxErrorMessage(e: unknown, fallback = 'Save failed.'): string {
  if (!(e instanceof ConsoleApiError)) return fallback;
  const body = e.body as Record<string, unknown> | undefined;
  const detail = typeof body?.detail === 'string' ? body.detail : '';
  const first = Array.isArray(body?.errors) ? (body.errors[0] as unknown) : undefined;
  if (detail && first && typeof first === 'object') {
    const { field, error } = first as { field?: unknown; error?: unknown };
    if (typeof error === 'string' && !detail.includes(error)) {
      return `${detail} (${typeof field === 'string' ? `${field}: ` : ''}${error})`;
    }
  }
  if (detail) return detail;
  for (const value of Object.values(body ?? {})) {
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    if (typeof value === 'string') return value;
  }
  return e.message || fallback;
}
